import type { UpdateApp } from "@biteasy/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { orpc } from "@/orpc/client";
import { SKIP } from "@/util/skip-token";

export type UseAutosaveSectionOptions<TValue> = {
	initialValue: TValue;
	buildPayload: (
		value: TValue,
	) => (Partial<UpdateApp> & { id: string }) | typeof SKIP;
	debounceMs?: number;
	onError: (err: unknown) => void;
};

export function useAutosaveSection<TValue>({
	initialValue,
	buildPayload,
	debounceMs = 500,
	onError,
}: UseAutosaveSectionOptions<TValue>) {
	const queryClient = useQueryClient();
	const [value, setValue] = useState(initialValue);

	useEffect(() => {
		setValue(initialValue);
	}, [initialValue]);

	const mutation = useMutation(
		orpc.apps.update.mutationOptions({
			onSuccess() {
				queryClient.invalidateQueries({
					queryKey: orpc.apps.detail.key(),
				});
			},
			onError,
		}),
	);

	const debouncedSave = useDebouncedCallback((next: TValue) => {
		const payload = buildPayload(next) as UpdateApp | typeof SKIP;
		if (payload === SKIP) return;
		mutation.mutate(payload);
		// Optimistically update the value
		setValue(next);
	}, debounceMs);

	function update(next: TValue) {
		setValue(next);
		debouncedSave(next);
	}

	return {
		value,
		update,
		isSaving: mutation.isPending,
		isSaved: mutation.isSuccess && !mutation.isPending,
		isError: mutation.isError,
	};
}
